// src/pages/TrustDomainPage.jsx
import React, { useEffect, useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import {
  ArrowLeft, Shield, ChevronRight, Globe, ChevronDown, ChevronUp,
  Search, Filter, X,
} from "lucide-react";
import useTrustStore from "../../store/useTrustStore";
import BackgroundFooter from "../BackgroundFooter";
import { Col, Row } from "react-bootstrap";
import NavigationBar from "../Nav";
import Menubar from "../Menubar";
import { useAuth } from "../../contexts/AuthContext";


/* ═══════════════════════════════════════════════
   BRAND COLORS (ServQual Brand Guidelines)
═══════════════════════════════════════════════ */
const BRAND = {
  primary:      "#6BCE8B",
  primaryDark:  "#4EAD6E",
  primaryLight: "#E8F8EE",
  secondary:    "#035EA5",
  secondaryDark:"#024A82",
  secondaryLight:"#E6F0F9",
  tertiary:     "#FFFF00",
  tertiaryMuted:"#FFF9C4",
  white:        "#FFFFFF",
  dark:         "#1E293B",
  gray50:       "#F8FAFC",
  gray100:      "#F1F5F9",
  gray200:      "#E2E8F0",
  gray300:      "#CBD5E1",
  gray400:      "#94A3B8",
  gray500:      "#64748B",
  gray600:      "#475569",
  gray900:      "#0F172A",
};

const severityColor = {
  HIGH:     { bg: "#FEE2E2", text: "#DC2626" },
  MEDIUM:   { bg: "#FEF9C3", text: "#A16207" },
  LOW:      { bg: BRAND.primaryLight, text: BRAND.primaryDark },
  CRITICAL: { bg: "#FDE8E8", text: "#991B1B" },
};


/* ─── Collapsible Compliance Coverage ─── */
function ComplianceCoverage({ domainCoverage }) {
  const [expanded, setExpanded] = useState(false);
  const [coverageSearch, setCoverageSearch] = useState("");
  const [regionFilter, setRegionFilter] = useState("ALL");

  const regions = useMemo(() => {
    const r = new Set();
    domainCoverage.forEach((fw) => { if (fw.region) r.add(fw.region); });
    return ["ALL", ...Array.from(r).sort()];
  }, [domainCoverage]);

  const filtered = useMemo(() => {
    let list = domainCoverage;
    if (coverageSearch) {
      const q = coverageSearch.toLowerCase();
      list = list.filter((fw) =>
        fw.framework?.toLowerCase().includes(q) ||
        fw.clauses?.some((c) => c.toLowerCase().includes(q))
      );
    }
    if (regionFilter !== "ALL") {
      list = list.filter((fw) => fw.region === regionFilter);
    }
    return list;
  }, [domainCoverage, coverageSearch, regionFilter]);

  // Group by region for compact display
  const groupedByRegion = useMemo(() => {
    const groups = {};
    domainCoverage.forEach((fw) => {
      const r = fw.region || "Global";
      if (!groups[r]) groups[r] = [];
      groups[r].push(fw);
    });
    return groups;
  }, [domainCoverage]);

  const regionSummary = Object.entries(groupedByRegion).map(([r, fws]) => ({
    region: r,
    count: fws.length,
  })).sort((a, b) => b.count - a.count);

  const PREVIEW_COUNT = 0; // collapsed = show summary only

  return (
    <div
      style={{
        background: BRAND.white, border: `1px solid ${BRAND.gray200}`,
        borderRadius: "14px", marginBottom: "20px",
        boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
        overflow: "hidden",
      }}
    >
      {/* Header — always visible, clickable */}
      <div
        onClick={() => setExpanded(!expanded)}
        style={{
          display: "flex", alignItems: "center", gap: "10px",
          padding: "14px 20px", cursor: "pointer",
          background: expanded ? BRAND.secondaryLight : BRAND.white,
          transition: "background 0.2s",
        }}
      >
        <Globe size={16} style={{ color: BRAND.secondary }} />
        <span style={{ fontSize: "15px", fontWeight: 600, color: BRAND.gray900, fontFamily: "'Inter', sans-serif" }}>
          Compliance Coverage
        </span>

        {/* Region summary badges — compact overview */}
        <div style={{ display: "flex", gap: "6px", marginLeft: "12px", flexWrap: "wrap", flex: 1 }}>
          {regionSummary.slice(0, 6).map((r) => (
            <span
              key={r.region}
              style={{
                padding: "2px 10px", borderRadius: "12px", fontSize: "11px",
                fontWeight: 500, fontFamily: "'Inter', sans-serif",
                background: r.region === "Global" ? `${BRAND.secondary}15` : `${BRAND.primary}18`,
                color: r.region === "Global" ? BRAND.secondary : BRAND.primaryDark,
                border: `1px solid ${r.region === "Global" ? `${BRAND.secondary}25` : `${BRAND.primary}30`}`,
              }}
            >
              {r.region} ({r.count})
            </span>
          ))}
          {regionSummary.length > 6 && (
            <span style={{ fontSize: "11px", color: BRAND.gray400, alignSelf: "center" }}>
              +{regionSummary.length - 6} more
            </span>
          )}
        </div>

        <span style={{
          fontSize: "12px", color: BRAND.secondary, fontWeight: 600,
          padding: "3px 12px", borderRadius: "12px",
          background: `${BRAND.secondary}10`, border: `1px solid ${BRAND.secondary}20`,
          fontFamily: "'Inter', sans-serif", whiteSpace: "nowrap",
        }}>
          {domainCoverage.length} frameworks
        </span>
        {expanded ? <ChevronUp size={16} style={{ color: BRAND.gray400 }} /> : <ChevronDown size={16} style={{ color: BRAND.gray400 }} />}
      </div>

      {/* Expanded content */}
      {expanded && (
        <div style={{ borderTop: `1px solid ${BRAND.gray200}` }}>
          {/* Search + Region Filter */}
          <div style={{
            display: "flex", gap: "10px", padding: "12px 20px",
            background: BRAND.gray50, borderBottom: `1px solid ${BRAND.gray100}`,
            flexWrap: "wrap", alignItems: "center",
          }}>
            <div style={{ position: "relative", flex: 1, minWidth: "200px" }}>
              <Search size={14} style={{ position: "absolute", left: "10px", top: "50%", transform: "translateY(-50%)", color: BRAND.gray400 }} />
              <input
                type="text" placeholder="Search frameworks..."
                value={coverageSearch} onChange={(e) => setCoverageSearch(e.target.value)}
                style={{
                  width: "100%", padding: "7px 10px 7px 30px", border: `1px solid ${BRAND.gray200}`,
                  borderRadius: "8px", fontSize: "12px", fontFamily: "'Inter', sans-serif",
                  outline: "none", background: BRAND.white,
                }}
              />
              {coverageSearch && (
                <button onClick={() => setCoverageSearch("")}
                  style={{ position: "absolute", right: "8px", top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer" }}>
                  <X size={12} style={{ color: BRAND.gray400 }} />
                </button>
              )}
            </div>
            <div style={{ display: "flex", gap: "4px", flexWrap: "wrap" }}>
              {regions.map((r) => (
                <button
                  key={r}
                  onClick={() => setRegionFilter(r)}
                  style={{
                    padding: "5px 12px", borderRadius: "8px", fontSize: "11px",
                    fontWeight: regionFilter === r ? 600 : 400,
                    fontFamily: "'Inter', sans-serif", cursor: "pointer",
                    border: `1px solid ${regionFilter === r ? BRAND.secondary : BRAND.gray200}`,
                    background: regionFilter === r ? BRAND.secondaryDark : BRAND.white,
                    color: regionFilter === r ? BRAND.white : BRAND.gray600,
                    transition: "all 0.15s",
                  }}
                >
                  {r === "ALL" ? "All Regions" : r}
                </button>
              ))}
            </div>
          </div>

          {/* Framework pills — scrollable max-height */}
          <div style={{
            padding: "14px 20px", maxHeight: "280px", overflowY: "auto",
            display: "flex", flexWrap: "wrap", gap: "6px",
          }}>
            {filtered.length === 0 && (
              <span style={{ fontSize: "12px", color: BRAND.gray400, padding: "20px 0", width: "100%", textAlign: "center" }}>
                No frameworks match your search.
              </span>
            )}
            {filtered.map((fw, i) => (
              <div
                key={i}
                style={{
                  display: "inline-flex", alignItems: "center", gap: "6px",
                  padding: "4px 10px", borderRadius: "8px", fontSize: "11px",
                  fontFamily: "'Inter', sans-serif",
                  background: BRAND.gray50, border: `1px solid ${BRAND.gray100}`,
                  transition: "border-color 0.15s",
                }}
                // onMouseEnter={(e) => e.currentTarget.style.borderColor = BRAND.primary}
                // onMouseLeave={(e) => e.currentTarget.style.borderColor = BRAND.gray100}
              >
                <span style={{
                  width: "6px", height: "6px", borderRadius: "50%", flexShrink: 0,
                  background: fw.status === "P" ? BRAND.secondary : fw.status === "S" ? BRAND.primary : BRAND.gray300,
                }} />
                <span style={{ color: BRAND.gray900, fontWeight: 500, whiteSpace: "nowrap" }}>{fw.framework}</span>
               {fw.clauses?.length > 0 && (
  <span style={{ color: BRAND.gray700, fontSize: 11, whiteSpace: "normal", lineHeight: 1.4 }}>
    ({fw.clauses.join("; ")})
  </span>
)}
                {fw.region && ( 
                  <span style={{
                    fontSize: "9px", padding: "1px 5px", borderRadius: "4px",
                    background: `${BRAND.secondary}10`, color: BRAND.secondary,
                    fontWeight: 500, whiteSpace: "nowrap",
                  }}>
                    {fw.region}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


/* ═══════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════ */
export default function TrustDomainPage({ userData }) {
  const { domainId } = useParams();
  const { profile } = useAuth();
  const {
    selectedDomain, domainControls, domainCoverage,
    loading, fetchDomainControls,
    searchQuery, setSearchQuery, severityFilter, setSeverityFilter,
    getFilteredControls, setProfile,
    applicableFrameworks,
    readinessData,fetchReadiness,
  } = useTrustStore();

  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() =>
    JSON.parse(localStorage.getItem("isSidebarCollapsed")) || false
  );
  const handleSidebarToggle = () => setIsSidebarCollapsed((prev) => !prev);
// ★ Filter coverage by applicable frameworks from overview
const filteredCoverage = useMemo(() => {
  if (!applicableFrameworks || applicableFrameworks.length === 0) return domainCoverage;
  const selected = applicableFrameworks.map((f) => f.toLowerCase());
  return domainCoverage.filter((fw) => {
    const fwName = (fw.framework || "").toLowerCase();
    return selected.some((sel) => fwName.includes(sel));
  });
}, [domainCoverage, applicableFrameworks]);
  // ★ Domain-level readiness from store
  const domainReadiness = useMemo(() => {
    // ★ Check domainBreakdown from readiness API response
    const readinessState = useTrustStore.getState();
    const breakdown = readinessState.readinessData?.domainBreakdown || [];
    
    const domainEntry = breakdown.find((r) => r.domainId === domainId);
    if (domainEntry) return domainEntry;

    // Fallback: calculate from domainControls locally
    const total = domainControls.length;
    if (total === 0) return { readinessPercent: 0, passCount: 0, partialCount: 0, gapCount: 0, total: 0 };

    let passCount = 0, partialCount = 0, gapCount = 0;
    domainControls.forEach((c) => {
      const status = (c.status || c.assessmentStatus || "").toUpperCase();
      if (status === "PASS") passCount++;
      else if (status === "PARTIAL") partialCount++;
      else if (status === "GAP") gapCount++;
    });

    return {
      readinessPercent: total > 0 ? Math.round((passCount / total) * 100) : 0,
      passCount,
      partialCount,
      gapCount,
      total,
    };
  }, [readinessData, domainId, domainControls]);
useEffect(() => {
  console.log("applicableFrameworks:", applicableFrameworks);
  console.log("domainCoverage sample:", domainCoverage.slice(0, 3));
  console.log("filteredCoverage:", filteredCoverage.length);
}, [applicableFrameworks, domainCoverage, filteredCoverage]);

  useEffect(() => {
    if (profile) {
      setProfile(profile);
      fetchDomainControls(domainId);
      fetchReadiness();
    }
  }, [profile, domainId]);

  const filteredControls = getFilteredControls().sort((a, b) => {
  const numA = parseInt((a.controlId || "").replace(/\D/g, ""), 10) || 0;
  const numB = parseInt((b.controlId || "").replace(/\D/g, ""), 10) || 0;
  return numA - numB;
});


   if (loading && !domainControls.length) {
    return (
      <div style={{
        minHeight: "100vh",
        display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center",
        background: BRAND.gray50, fontFamily: "'Inter', sans-serif",
        gap: "16px",
      }}>
        <div style={{
          width: "48px", height: "48px", borderRadius: "50%",
          border: `3px solid ${BRAND.gray200}`,
          borderTopColor: BRAND.primary,
          animation: "spin 0.8s linear infinite",
        }} />
        <div style={{ textAlign: "center" }}>
          <p style={{ fontSize: "14px", fontWeight: 600, color: BRAND.gray900, margin: "0 0 4px" }}>
            Loading Domain Controls
          </p>
          <p style={{ fontSize: "12px", color: BRAND.gray400, margin: 0 }}>
            Fetching {domainId} data...
          </p>
        </div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }


  return (
    <Row className="g-0">
      <Col
        xs={12}
        className={`bg-transparent sidebar-container ${isSidebarCollapsed ? "collapsed" : "hello"}`}
        style={{ width: isSidebarCollapsed ? "90px" : "317px", transition: "width 0.3s ease" }}
      >
        <Menubar isCollapsed={isSidebarCollapsed} onToggle={handleSidebarToggle} />
      </Col>

      <Col
        className="pt-0 px-0 ps-0 section-container"
        style={{ width: `calc(100% - ${isSidebarCollapsed ? "90px" : "317px"})`, transition: "width 0.3s ease" }}
      >
        <NavigationBar userData={userData} isSidebarCollapsed={isSidebarCollapsed} />
        <Col className="overflow-auto p-0" style={{ height: "calc(100vh - 60px)" }}>
          <div style={{ background: BRAND.gray50, color: BRAND.gray900, fontFamily: "'Inter', sans-serif" }}>
            <div style={{ maxWidth: "1280px", margin: "0 auto", padding: "28px 24px" }}>

              {/* Back Link */}
              <Link
                to="/trust"
                style={{
                  display: "inline-flex", alignItems: "center", gap: "6px",
                  fontSize: "13px", color: BRAND.secondary, textDecoration: "none",
                  marginBottom: "20px", fontWeight: 500,
                }}
              >
                <ArrowLeft size={15} /> Back to Trust Dashboard
              </Link>

              {/* Domain Header */}
              <div style={{
                display: "flex", alignItems: "center", gap: "12px",
                marginBottom: "20px", flexWrap: "wrap",
              }}>
                {/* <div style={{
                  width: "40px", height: "40px", borderRadius: "10px",
                  background: BRAND.secondaryLight, display: "flex",
                  alignItems: "center", justifyContent: "center",
                }}>
                  <Shield size={20} style={{ color: BRAND.secondary }} />
                </div> */}
                <div>
                  {/* <span style={{ fontSize: "11px", color: BRAND.secondary, fontFamily: "'Inter', monospace", fontWeight: 600 }}>
                    {domainId}
                  </span> */}
                  <h1 style={{ margin: 0, fontSize: "20px", fontWeight: 700, color: BRAND.gray900, fontFamily: "'Inter', sans-serif" }}>
                    {selectedDomain?.domainName || domainId}
                  </h1>
                </div>
                <div style={{ marginLeft: "auto", display: "flex", gap: "8px" }}>
                  <span style={{
                    padding: "5px 14px", fontSize: "12px", borderRadius: "20px",
                    background: BRAND.white, border: `1px solid ${BRAND.gray200}`,
                    color: BRAND.gray600, fontWeight: 500, fontFamily: "'Inter', sans-serif",
                  }}>
                    {selectedDomain?.controlCount || domainControls.length} controls
                  </span>
                 <span style={{
  padding: "5px 14px", fontSize: "12px", borderRadius: "20px",
  background: BRAND.secondaryLight, border: `1px solid ${BRAND.secondary}25`,
  color: BRAND.secondary, fontWeight: 600, fontFamily: "'Inter', sans-serif",
}}>
  {filteredCoverage.length} frameworks
</span>

                </div>
              </div>

              {/* Compliance Coverage — Collapsible */}
             {/* NEW */}
{filteredCoverage.length > 0 && (
  <ComplianceCoverage domainCoverage={filteredCoverage} />
)}
            

              {/* ★ Domain Readiness Bar */}
              {domainReadiness && (
                <div style={{
                  background: BRAND.white,
                  border: `1px solid ${BRAND.gray200}`,
                  borderRadius: "14px",
                  marginBottom: "20px",
                  padding: "16px 20px",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
                }}>
                  <div style={{
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    marginBottom: "12px",
                  }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <Shield size={16} style={{ color: BRAND.secondary }} />
                      <span style={{
                        fontSize: "15px", fontWeight: 600,
                        color: BRAND.gray900, fontFamily: "'Inter', sans-serif",
                      }}>
                        Domain Readiness
                      </span>
                    </div>
                    <span style={{
                      fontSize: "20px", fontWeight: 800,
                      color: domainReadiness.readinessPercent >= 80 ? BRAND.primaryDark
                        : domainReadiness.readinessPercent >= 50 ? "#A16207"
                        : domainReadiness.readinessPercent > 0 ? "#DC2626"
                        : BRAND.gray400,
                      fontFamily: "'Inter', sans-serif",
                    }}>
                      {domainReadiness.readinessPercent}%
                    </span>
                  </div>

                  {/* Progress bar */}
                  <div style={{
                    height: "10px", borderRadius: "5px",
                    background: BRAND.gray200,
                    overflow: "hidden",
                    marginBottom: "12px",
                  }}>
                    {/* Stacked: Pass (green) + Partial (yellow) + Gap (red) */}
                    <div style={{ display: "flex", height: "100%" }}>
                      {domainReadiness.passCount > 0 && (
                        <div style={{
                          width: `${(domainReadiness.passCount / (domainReadiness.total || domainControls.length || 1)) * 100}%`,
                          background: BRAND.primary,
                          transition: "width 0.5s ease",
                        }} />
                      )}
                      {domainReadiness.partialCount > 0 && (
                        <div style={{
                          width: `${(domainReadiness.partialCount / (domainReadiness.total || domainControls.length || 1)) * 100}%`,
                          background: "#F59E0B",
                          transition: "width 0.5s ease",
                        }} />
                      )}
                      {domainReadiness.gapCount > 0 && (
                        <div style={{
                          width: `${(domainReadiness.gapCount / (domainReadiness.total || domainControls.length || 1)) * 100}%`,
                          background: "#EF4444",
                          transition: "width 0.5s ease",
                        }} />
                      )}
                    </div>
                  </div>

                  {/* Status counts */}
                  <div style={{
                    display: "flex", gap: "16px", fontSize: "12px",
                    fontFamily: "'Inter', sans-serif", flexWrap: "wrap",
                  }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                      <span style={{
                        width: "8px", height: "8px", borderRadius: "50%",
                        background: BRAND.primary, display: "inline-block",
                      }} />
                      <span style={{ color: BRAND.gray600 }}>
                        Pass <strong style={{ color: BRAND.primaryDark }}>{domainReadiness.passCount}</strong>
                      </span>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                      <span style={{
                        width: "8px", height: "8px", borderRadius: "50%",
                        background: "#F59E0B", display: "inline-block",
                      }} />
                      <span style={{ color: BRAND.gray600 }}>
                        Partial <strong style={{ color: "#A16207" }}>{domainReadiness.partialCount}</strong>
                      </span>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                      <span style={{
                        width: "8px", height: "8px", borderRadius: "50%",
                        background: "#EF4444", display: "inline-block",
                      }} />
                      <span style={{ color: BRAND.gray600 }}>
                        Gap <strong style={{ color: "#DC2626" }}>{domainReadiness.gapCount}</strong>
                      </span>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                      <span style={{
                        width: "8px", height: "8px", borderRadius: "50%",
                        background: BRAND.gray300, display: "inline-block",
                      }} />
                      <span style={{ color: BRAND.gray600 }}>
                        Not Assessed <strong style={{ color: BRAND.gray500 }}>
                          {(domainReadiness.total || domainControls.length) - domainReadiness.passCount - domainReadiness.partialCount - domainReadiness.gapCount}
                        </strong>
                      </span>
                    </div>
                  </div>
                </div>
              )}

             

              {/* Search & Severity Filter */}
              <div style={{
                display: "flex", gap: "10px", marginBottom: "16px",
                flexWrap: "wrap", alignItems: "center",
              }}>
                <div style={{ position: "relative", flex: 1, minWidth: "250px" }}>
                  <Search size={14} style={{
                    position: "absolute", left: "12px", top: "50%",
                    transform: "translateY(-50%)", color: BRAND.gray400,
                  }} />
                  <input
                    type="text" placeholder="Search controls..."
                    value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                    style={{
                      width: "100%", padding: "9px 12px 9px 34px",
                      border: `1px solid ${BRAND.gray200}`, borderRadius: "10px",
                      fontSize: "13px", fontFamily: "'Inter', sans-serif",
                      outline: "none", background: BRAND.white,
                      transition: "border-color 0.2s",
                    }}
                    onFocus={(e) => e.target.style.borderColor = BRAND.primary}
                    onBlur={(e) => e.target.style.borderColor = BRAND.gray200}
                  />
                </div>
                <select
                  value={severityFilter}
                  onChange={(e) => setSeverityFilter(e.target.value)}
                  style={{
                    padding: "9px 14px", border: `1px solid ${BRAND.gray200}`,
                    borderRadius: "10px", fontSize: "13px", fontFamily: "'Inter', sans-serif",
                    background: BRAND.white, color: BRAND.gray900, outline: "none",
                    cursor: "pointer",
                  }}
                >
                  <option value="ALL">All Severities</option>
                  <option value="CRITICAL">Critical</option>
                  <option value="HIGH">High</option>
                  <option value="MEDIUM">Medium</option>
                  <option value="LOW">Low</option>
                </select>
              </div>

              {/* Controls Table */}
              <div style={{
                background: BRAND.white, border: `1px solid ${BRAND.gray200}`,
                borderRadius: "14px", overflow: "hidden",
                boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
              }}>
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", fontSize: "13px", borderCollapse: "collapse", minWidth: "1000px", fontFamily: "'Inter', sans-serif" }}>
                    <thead>
                      <tr style={{ borderBottom: `1px solid ${BRAND.gray200}`, background: BRAND.gray50 }}>
                        {[
                          { label: "Control ID", w: "100px" },
                          { label: "Name", w: "180px" },
                          { label: "Category", w: "130px" },
                          { label: "Severity", w: "90px" },
                          { label: "Applicability (SOA) Question", w: "auto" },
                          { label: "Frameworks", w: "90px" },
                        //   { label: "Source", w: "80px" },
                          { label: "Action", w: "70px" },
                        ].map((col) => (
                          <th
                            key={col.label}
                            style={{
                              textAlign: "left", padding: "10px 14px", fontSize: "11px",
                              fontWeight: 600, color: BRAND.gray900, textTransform: "uppercase",
                              letterSpacing: "0.5px", whiteSpace: "nowrap", width: col.w,
                            }}
                          >
                            {col.label}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {filteredControls.map((c) => {
                        const sev = severityColor[c.severity] || { bg: BRAND.gray100, text: BRAND.gray500 };
                        return (
                          <tr
                            key={c.controlId}
                            style={{
                              borderBottom: `1px solid ${BRAND.gray100}`,
                              transition: "background 0.15s",
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.background = BRAND.gray50}
                            onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                          >
                            <td style={{ padding: "10px 14px", whiteSpace: "nowrap", fontSize: "12px" }}>
  <Link
    to={`/trust/control/${c.controlId}`}
    style={{
      fontFamily: "'Inter', monospace",
      color: BRAND.secondary,
      fontWeight: 600,
      textDecoration: "none",
      cursor: "pointer",
    }}
    onMouseEnter={(e) => e.currentTarget.style.textDecoration = "underline"}
    onMouseLeave={(e) => e.currentTarget.style.textDecoration = "none"}
  >
    {c.controlId}
  </Link>
</td>

                            <td style={{ padding: "10px 14px", color: BRAND.gray900, fontWeight: 500 }}>
                              {c.controlName}
                            </td>
                            <td style={{ padding: "10px 14px", color: BRAND.gray500, whiteSpace: "nowrap" }}>
                              {c.category}
                            </td>
                            <td style={{ padding: "10px 14px", whiteSpace: "nowrap" }}>
                              <span style={{
                                padding: "3px 10px", fontSize: "11px", borderRadius: "6px",
                                fontWeight: 600, background: sev.bg, color: sev.text,
                              }}>
                                {c.severity}
                              </span>
                            </td>
                            <td style={{
                              padding: "10px 14px", color: BRAND.gray500, maxWidth: "280px",
                              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                            }} title={c.question}>
                              {c.question || "—"}
                            </td>
                            <td style={{ padding: "10px 14px", color: BRAND.gray500, textAlign: "center" }}>
                              {c.frameworkCount || 0}
                            </td>
                            {/* <td style={{ padding: "10px 14px", whiteSpace: "nowrap" }}>
                              <span style={{
                                padding: "2px 8px", fontSize: "10px", borderRadius: "5px",
                                fontWeight: 600,
                                background: c.mappingSource === "CONTROL_LEVEL" ? `${BRAND.secondary}12` : BRAND.gray100,
                                color: c.mappingSource === "CONTROL_LEVEL" ? BRAND.secondary : BRAND.gray500,
                              }}>
                                {c.mappingSource === "CONTROL_LEVEL" ? "Control" : "Inherited"}
                              </span>
                            </td> */}
                            <td style={{ padding: "10px 14px", whiteSpace: "nowrap" }}>
                              <Link
                                to={`/trust/control/${c.controlId}`}
                                style={{
                                  display: "inline-flex", alignItems: "center", gap: "3px",
                                  padding: "4px 10px", fontSize: "12px", borderRadius: "6px",
                                  color: BRAND.secondary, textDecoration: "none", fontWeight: 500,
                                  transition: "background 0.15s",
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.background = BRAND.secondaryLight}
                                onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                              >
                                View <ChevronRight size={14} />
                              </Link>
                            </td>
                          </tr>
                        );
                      })}
                      {filteredControls.length === 0 && (
                        <tr>
                          <td colSpan={8} style={{ padding: "40px 20px", textAlign: "center", color: BRAND.gray400, fontSize: "13px" }}>
                            No controls found for this domain.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

            </div>
          </div>
          <BackgroundFooter isSidebarCollapsed={isSidebarCollapsed} />
        </Col>
      </Col>
    </Row>
  );
}
